// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import ErrorBoundary from './components/ErrorBoundary';
import { useNavigate } from 'react-router-dom';
import { syncFCMToken, setupForegroundNotifications, startTokenHeartbeat } from './utils/fcm';
import Home from './pages/Home';
import Profile from './pages/Profile';
import Contact from './pages/Contact';
import SearchProviders from './pages/SearchProviders';
import ProviderProfile from './pages/ProviderProfile';
import ManageOrders from './pages/ManageOrders';
import ForgotPassword from './pages/ForgotPassword';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import NotFound from './pages/NotFound';
import Blog from './pages/Blog';
import ArticlePage from './pages/ArticlePage';
import AdminArticlePage from './pages/AdminArticlePage';
import Notifications from './components/Notifications';
import CategoriesList from './pages/CategoriesList';
import CategoryHub from './pages/CategoryHub';
import CategoryLocation from './pages/CategoryLocation';
import './index.css';

const queryClient = new QueryClient();

function AppContent({ chatSocket, setChatSocket }) {
  const navigate = useNavigate();
  const location = useLocation();

  const { data: user, isSuccess, error: userError } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/profile/`, { withCredentials: true });
      return response.data;
    },
    retry: false,
  });

  const [isPWA, setIsPWA] = useState(false);

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsPWA(isStandalone);
  }, []);

  // FCM setup (token sync, heartbeat, foreground messages) — no prompt logic
  useEffect(() => {
    let stopHeartbeat;
    let unsubscribe;

    const setupFCM = async () => {
      try {
        await syncFCMToken(user);
        stopHeartbeat = await startTokenHeartbeat(user);
        unsubscribe = setupForegroundNotifications(navigate);
      } catch (err) {
        console.error('[App] Error in FCM setup:', err);
      }
    };

    setupFCM();

    return () => {
      if (unsubscribe) unsubscribe();
      if (stopHeartbeat) stopHeartbeat();
    };
  }, [user, navigate]);

  // WebSocket cleanup
  useEffect(() => {
    if (!isSuccess) return;

    if (!user) {
      if (chatSocket?.readyState === WebSocket.OPEN) chatSocket.close();
      setChatSocket(null);
      return;
    }

    return () => {
      if (chatSocket?.readyState === WebSocket.OPEN) chatSocket.close();
    };
  }, [user, isSuccess, chatSocket, setChatSocket]);

  useEffect(() => {
    if (window.gtag) {
      window.gtag('config', 'G-HDE3BPR4FP', {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);

  return (
    <ErrorBoundary>
      <Helmet>
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-HDE3BPR4FP"></script>
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-HDE3BPR4FP');
          `}
        </script>
      </Helmet>
      <div className="App">
        <Notifications userId={user?.id} />
        <Routes>
          <Route
            path="/"
            element={
              <ErrorBoundary>
                <Home />
              </ErrorBoundary>
            }
          />
          <Route path="/profile" element={<Profile chatSocket={chatSocket} setChatSocket={setChatSocket} />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/search" element={<SearchProviders />} />
          <Route path="/provider/:providerId" element={<ProviderProfile />} />
          <Route path="/manage-orders" element={<ManageOrders />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/categories" element={<CategoriesList />} />
          <Route path="/categories/:slug" element={<CategoryHub />} />
          <Route path="/categories/:slug/:location" element={<CategoryLocation />} />
          <Route path="/blog" element={<Blog user={user} />} />
          <Route path="/blog/:id" element={<ArticlePage user={user} />} />
          <Route path="/blog/new" element={<AdminArticlePage user={user} />} />
          <Route path="/blog/edit/:id" element={<AdminArticlePage user={user} />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </ErrorBoundary>
  );
}

function App() {
  const [chatSocket, setChatSocket] = useState(null);

  return (
    <QueryClientProvider client={queryClient}>
      <Router basename="/">
        <AppContent chatSocket={chatSocket} setChatSocket={setChatSocket} />
      </Router>
    </QueryClientProvider>
  );
}

export default App;
